import { TFunction } from "i18next";

import {
  BOOKING_UPLOAD_COLUMNS,
  BOOKING_UPLOAD_COLUMNS_KEY,
  firstLetterToLowerCase,
  normalizeTextForMatch,
} from "@ryde/shared";

/**
 * Builds the lookup used to translate a file header into the API field name the backend
 * expects. Every column is registered twice: under its canonical English name and under
 * its label in the active language, so a Hebrew file ("עובד", "תאריך", …) and an English
 * one ("worker", "date", …) both resolve while the API contract stays English-only.
 */
export const getBulkUploadBookingColumnsMap = (
  t: TFunction,
): Map<string, string> => {
  const columnsMap = new Map<string, string>();

  for (const columnKey of Object.values(BOOKING_UPLOAD_COLUMNS)) {
    const translationKey = `${BOOKING_UPLOAD_COLUMNS_KEY}.${columnKey}`;
    const translatedColumn = t(translationKey);

    columnsMap.set(normalizeTextForMatch(columnKey), columnKey);

    // A missing translation resolves to the key itself - registering that would let a
    // header named after the key path win over a real column name.
    if (translatedColumn && translatedColumn !== translationKey) {
      columnsMap.set(normalizeTextForMatch(translatedColumn), columnKey);
    }
  }

  return columnsMap;
};

/**
 * Unrecognized headers keep their original name (only the first-letter case is normalized,
 * as before) so extra columns behave exactly as they did before localized headers existed.
 */
export const resolveBulkUploadBookingColumnKey = ({
  header,
  columnsMap,
}: {
  header: string;
  columnsMap: Map<string, string>;
}): string => {
  return (
    columnsMap.get(normalizeTextForMatch(header)) ??
    firstLetterToLowerCase(header)
  );
};
